import { Component, Input, OnInit, EventEmitter, Output, OnDestroy } from "@angular/core";
import { Utilities } from "@tylertech/shared-module";
import { IMetadataAction, IMetadataChipAction } from "@tylertech/tyler-api-types";
import { IMenuOption } from "@tylertech/forge";
import { RepositoryFormData } from "../../interfaces";
import { MetadataActionService, MetadataService, MetadataEventService } from "../../services";
import { MetadataActionHelpers } from "../../utilities";
import { SecurityService, KeyHandlerService } from "@tylertech/core-module";
import { ActionCallBackArgs } from "./metadataAction.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
@Component({
    selector: "metadata-action-menu",
    templateUrl: "./metadataActionMenu.tmpl.html",
    standalone: false
})
export class MetadataActionMenuComponent implements OnInit, OnDestroy
{
    @Input() metadataActions: IMetadataAction[];

    @Input() model: any;

    @Input() formData: RepositoryFormData;

    @Input() expansionPanelAction: boolean;

    @Output() actionCallback = new EventEmitter<ActionCallBackArgs>();

    private listenerId: string;
    private compactMode: boolean = false;

    constructor(
        private readonly $metadataService: MetadataService,
        private readonly $metadataActionService: MetadataActionService,
        private readonly $keyHandlerService: KeyHandlerService,
        private $metadataEventService: MetadataEventService,
        private readonly $securityService: SecurityService
    )
    {
        const self = this;
        this.$metadataEventService.onRecalculateWorking.pipe(takeUntilDestroyed())
            .subscribe(arg =>
            {
                if (!arg.Recalculating) self.loadMenuOptions();
            });
    }

    ngOnInit(): void
    {
        this.listenerId = Utilities.generateRandomGuid();
        this.loadMenuOptions();
        this.compactMode = this.$securityService.IsCompactMode;
    }

    ngOnDestroy(): void
    {
        this.$keyHandlerService.unsubscribeAllFromClass(this.listenerId);
    }

    get hasOverflowActions(): boolean
    {
        return !Utilities.IsNullOrUndefined(this.overflowActions) && this.overflowActions.length > 0;
    }

    get actions(): IMetadataAction[]
    {
        if (!this.metadataActions) return [];
        return this.metadataActions.filter(
            x =>
                !this.$metadataService.GetPermissionDenied(x.Hidden, x.HiddenProperty, this.model, this.formData) &&
                x.Context !== "Multiselect" &&
                !MetadataActionHelpers.isMultiSelectAction(x)
        );
    }

    get primaryActions(): IMetadataAction[]
    {
        return this.actions.filter(x => x.Primary);
    }

    get overflowActions(): IMetadataAction[]
    {
        return this.actions.filter(x => !x.Primary);
    }

    private _menuOptions: IMenuOption[];
    get menuOptions(): IMenuOption[]
    {
        return this._menuOptions;
    }

    loadMenuOptions(): void
    {
        this._menuOptions = [];
        this.$keyHandlerService.unsubscribeAllFromClass(this.listenerId);
        let parent = this.model ? this.model["$$Parent"] : undefined;
        for (let i = 0, l = this.overflowActions.length; i < l; i++)
        {
            if (this.$metadataActionService.IsHidden(this.overflowActions[i], this.model))
                continue;
            let disabled = this.$metadataActionService.IsDisabled(this.overflowActions[i], this.model, parent);
            this.registerKeyboardShortcut(disabled, this.overflowActions[i]);
            this._menuOptions.push({
                value: this.overflowActions[i].Label,
                label: this.$metadataService.EvaluateString(this.overflowActions[i].Label, parent, this.model),
                disabled: disabled
            });
            if (!this.$metadataActionService.HasSubitems(this.overflowActions[i]))
                continue;

            const subMenuItems: IMenuOption[] = [];
            const subItems = this.$metadataActionService.GetSubitems(this.overflowActions[i]);
            subItems.forEach((x) =>
            {
                disabled = this.$metadataActionService.IsDisabled(x, this.model, parent);
                if (!this.$metadataActionService.IsHidden(x, this.model, parent))
                {
                    subMenuItems.push({
                        value: x.Label,
                        label: this.$metadataService.EvaluateString(x.Label, parent, this.model),
                        disabled: disabled
                    });
                    this.registerKeyboardShortcut(disabled, x);
                }
            });
            this._menuOptions[i].options = subMenuItems;
        }
    }

    menuOpened(e): void
    {
        e.target.activateFirstOption();
    }

    isChip(action: IMetadataChipAction): boolean
    {
        return action.IsChip;
    }

    menuItemSelected(e): void
    {
        let action: IMetadataAction;

        if (!Utilities.IsNullOrUndefined(e.detail.parentValue))
        {
            action = this.overflowActions.filter(x => x.Label === e.detail.parentValue)[0].SubmenuActions.filter(x => x.Label === e.detail.value)[0];
        }
        else
        {
            action = this.overflowActions.filter(x => x.Label === e.detail.value)[0];
        }

        this.executeAction(action);
    }

    executeAction(action: IMetadataAction): void
    {
        if (this.$metadataActionService.OpenNewTabIfNeeded(this.model, action) === true)
        {
            return;
        }

        const self = this;
        this.$metadataActionService.ExecuteAction(this.model, action, this.formData)
            .then(result =>
            {
                if (!Utilities.IsNullOrUndefined(self.actionCallback))
                {
                    self.actionCallback.emit({
                        action: action,
                        result,
                    });
                }
            });
    }

    callback(callback: ActionCallBackArgs): void
    {
        if (Utilities.IsNullOrUndefined(this.actionCallback)) return;
        this.actionCallback.emit(callback);
    }

    registerKeyboardShortcut(disabled: boolean, action: IMetadataAction): void
    {
        if (disabled)
        {
            if (action.ShortcutMetadata)
                this.$keyHandlerService.unsubscribe(action.ShortcutMetadata.Key, action.ShortcutMetadata.ModifierKey);
        }

        else
            this.$keyHandlerService.registerShortcutMetadata(this.listenerId, action.ShortcutMetadata, `Menu Action|${action.Label}`, () => this.executeAction(action));
    }
}
